import { TableRow } from '@contentful/rich-text-types'
import { css, SerializedStyles } from '@emotion/core'
import React from 'react'

import { breakpoints, longCopyMobileBreakPoint } from '../../constants/breakPoints'
import { isMobile } from '../../helpers/common'
import { processTableData } from '../../helpers/contentful'
import SvgIcon from '../SvgIcon'
import ContentfulTablePopup from './ContentfulTablePopup'

interface ContentfulTableProps {
  content: TableRow[]
  preventOnClickHandler?: boolean
  cssStyles?: SerializedStyles
}

const ContentfulTable: React.FunctionComponent<ContentfulTableProps> = ({
  content,
  preventOnClickHandler,
  cssStyles = css``,
}) => {
  const [showModal, setShowModal] = React.useState(false)

  const onClick = () => {
    setShowModal(true)
    document.body.style.overflow = 'hidden'
  }

  const isMobileWidth = isMobile(longCopyMobileBreakPoint)

  const { tableHeader, tableBody } = processTableData(content)

  const renderTableCellContent = (cellString: string) => {
    switch (cellString) {
      case '✓':
        return (
          <div css={cmsTableSvgStyle}>
            <SvgIcon src={'signs/checkmark'} aria-label={'Feature included'} />
          </div>
        )
      case '✗':
        return (
          <div css={cmsTableSvgStyle}>
            <SvgIcon src={'signs/cross'} aria-label={'Feature not included'} />
          </div>
        )
      default:
        return cellString
    }
  }

  return (
    <React.Fragment>
      <div
        className={isMobile(breakpoints.small) ? 'full-screen-relative' : undefined}
        css={[cmsTableContainerStyle(preventOnClickHandler), cssStyles]}
        onClick={preventOnClickHandler || isMobileWidth ? undefined : onClick}
      >
        <table css={cmsTableStyle(tableHeader && tableHeader.length > 0)}>
          {tableHeader && tableHeader.length > 0 && (
            <thead>
              <tr>
                {tableHeader.map((header, index) => (
                  <th key={`cms-table-header-${index}`} className={'strong-body-2'} scope={'col'}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {tableBody.map((row, rowIndex) => (
              <tr key={`cms-table-row-${rowIndex}`}>
                {row.map((cell, cellIndex) => (
                  <td key={`cms-table-cell-${rowIndex}-${cellIndex}`} className={'body-2'}>
                    {renderTableCellContent(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {
        /* Popup does not make sense anymore if the table spans across full screen width anyway  => breakpoint */
        !isMobileWidth && (
          <ContentfulTablePopup
            content={content}
            showModal={showModal}
            setShowModal={setShowModal}
            preventOnClickHandler
          />
        )
      }
    </React.Fragment>
  )
}

export default ContentfulTable

const cmsTableContainerStyle = (preventOnClickHandler?: boolean) => css`
  margin: 2rem auto;
  width: 100%;
  overflow-x: auto;

  ${preventOnClickHandler ? '' : 'cursor: pointer;'};

  &::-webkit-scrollbar {
    display: none;
  }

  /* for firefox */
  scrollbar-width: none;

  @media screen and (max-width: ${breakpoints.small}px) {
    position: relative;
  }
`

const cmsTableStyle = (withTableHeader: boolean) => css`
  min-width: 100%;
  border-radius: 0.5rem;
  table-layout: fixed;
  border-collapse: collapse;
  margin: 0 auto;

  th,
  td {
    min-width: 100px;
    padding: 8px;
    text-align: left;
    vertical-align: top;
    border: 1px solid #efeff0;
  }

  td:has(svg) {
    vertical-align: middle;
  }

  th {
    background-color: #efeff0;
    &:not(:last-child) {
      border-right: 1px solid #fafbfc;
    }
  }

  tr {
    background-color: #fff;

    &:nth-of-type(${withTableHeader ? 'even' : 'odd'}) {
      background-color: #fafbfc;
    }
  }
`

const cmsTableSvgStyle = css`
  display: flex;
  width: 1.25rem;
`
